import { useState, useEffect } from 'react';
import { Menu } from 'lucide-react';
import { LogPulseLogo } from './LogPulseLogo';
import { buttonVariants } from './ui/Button';
import { ThemeToggle } from './ThemeToggle';
import { LanguageSelector } from './LanguageSelector';
import { MobileMenu } from './MobileMenu';
import { useT } from '@/i18n/provider';
import { LocalizedAnchor } from './LocalizedLink';
import { cn } from '@/lib/utils';
import { SITE_LOCALE } from '@/lib/site-config';
import { trackEvent } from '@/lib/analytics';

export function LandingHeader() {
  const t = useT();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  return (
    <>
      <header
        className={cn(
          'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
          scrolled
            ? 'glass shadow-sm'
            : 'bg-transparent'
        )}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <LocalizedAnchor href="/" className="flex items-center gap-2.5 group">
              <LogPulseLogo className="h-8 w-8" />
              <span className="text-xl font-bold text-gray-900 dark:text-white">
                LogPulse
              </span>
            </LocalizedAnchor>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-1">
              <LocalizedAnchor
                href="/#features"
                className="px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors rounded-lg hover:bg-gray-100/50 dark:hover:bg-gray-800/50"
              >
                {t('nav.features')}
              </LocalizedAnchor>
              <LocalizedAnchor
                href="/#pricing"
                className="px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors rounded-lg hover:bg-gray-100/50 dark:hover:bg-gray-800/50"
              >
                {t('nav.pricing')}
              </LocalizedAnchor>
              <a
                href="/docs"
                className="px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors rounded-lg hover:bg-gray-100/50 dark:hover:bg-gray-800/50"
              >
                {t('footer.docs')}
              </a>
              <a
                href="/blog"
                className="px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors rounded-lg hover:bg-gray-100/50 dark:hover:bg-gray-800/50"
              >
                Blog
              </a>
              <LocalizedAnchor
                href="/community"
                className="px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors rounded-lg hover:bg-gray-100/50 dark:hover:bg-gray-800/50"
              >
                Community
              </LocalizedAnchor>
            </nav>

            {/* Desktop actions */}
            <div className="hidden md:flex items-center gap-2">
              <LanguageSelector />
              <ThemeToggle />
              <a
                href="https://app.logpulse.io/sign-in"
                className="px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                {t('nav.signIn')}
              </a>
              {SITE_LOCALE === 'nl' && (
                <a
                  href="https://booking.logpulse.io/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('book_demo_click', { surface: 'header' })}
                  className={buttonVariants({ variant: 'outline', size: 'sm' })}
                >
                  {t('nav.bookDemo')}
                </a>
              )}
              <a href="https://app.logpulse.io/sign-in" className={buttonVariants({ variant: 'gradient', size: 'sm' })}>
                {t('nav.getStarted')}
              </a>
            </div>

            {/* Mobile hamburger */}
            <div className="flex md:hidden items-center gap-2">
              <ThemeToggle />
              <button
                onClick={() => setMobileOpen(true)}
                className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                aria-label="Open menu"
              >
                <Menu className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} />
    </>
  );
}
